import {Component, Injector, OnInit} from "@angular/core";
import {BaseComponentDirective} from "../../_shared/base/base.component";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ParameterService} from "../../_services/parameter.service";
import {NewCertificationService} from "./services/new-certification.service";
import {AssociationService} from "../associactions/services/association.service";
import {ConfirmationDialog} from "../../_shared/dialog/confirmation.dialog";

@Component({
    selector: 'app-new-certification-domain-edit',
    templateUrl: './new-certification-domain-edit.component.html',
    styleUrls: ['../../scss/office.scss']
})
export class NewCertificationDomainEditComponent extends BaseComponentDirective implements OnInit {
    public form: FormGroup;

    attendanceTypes:any = {};
    associations: any[] = [];
    certification: any = null;
    certDomains: any[] = [];
    domainId: number = null;

    constructor(
        injector: Injector,
        private formBuilder: FormBuilder,
        private parameterService: ParameterService,
        private certificationService: NewCertificationService,
        private associationService: AssociationService
    ) {
        super(injector);
        this.form = this.formBuilder.group({
            certificationTitle: [null, [Validators.required]],
            providerId: [null, [Validators.required]],
            domains: this.formBuilder.array([]),
            attendanceTypes: [[], []],
            conditions: [[], []]
        });

        this.addDomain();
    }

    get domains(): FormArray {
        return this.form.get('domains') as FormArray;
    }

    objectives(domainIndex: number): FormArray {
        return this.domains.at(domainIndex).get('objectives') as FormArray;
    }

    removedObjectives(domainIndex: number): FormArray {
        return this.domains.at(domainIndex).get('removedObjectives') as FormArray;
    }

    newObjectives(domainIndex: number): FormArray {
        return this.domains.at(domainIndex).get('newObjectives') as FormArray;
    }


    addDomain() {
        const domainFG = this.formBuilder.group({
            id: [null, [Validators.required]],
            domainName: [null, [Validators.required]],
            objectives: this.formBuilder.array([]),
            removedObjectives: this.formBuilder.array([]),
            newObjectives: this.formBuilder.array([])
        })
        this.domains.push(domainFG);
    }

    addObjective(domainIndex: number) {
        const objectiveFG = this.formBuilder.group({
            id: [null, []],
            objectiveName: [null, [Validators.required]],
            term: [null, [Validators.required]],
        });

        this.objectives(domainIndex).push(objectiveFG);
    }

    addRemovedObjective(domainIndex: number, existingObjectiveFG: any) {
        const objectiveFG = this.formBuilder.group({
            id: [existingObjectiveFG.id, []],
            objectiveName: [existingObjectiveFG.objectiveName, []],
            term: [existingObjectiveFG.term, []],
        });

        this.removedObjectives(domainIndex).push(objectiveFG);
    }

    addNewObjective(domainIndex: number, objectiveName: string, term: string) {
        const objectiveFG = this.formBuilder.group({
            objectiveName: [objectiveName, []],
            term: [term, []],
        });

        this.newObjectives(domainIndex).push(objectiveFG);
    }

    override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        this.attendanceTypes = await this.parameterService.getParameterByName("ATTENDANCE_TYPE");
        this.associations = await this.associationService.getAllNoPaging();
        this.certification = await this.certificationService.getById(this.activatedRoute.snapshot.params.certificationId);
        this.certDomains = await this.certificationService.getDomainsByCertificationId(this.certification.id);
        this.domainId = this.activatedRoute.snapshot.params.domainId;

        this.form.controls.certificationTitle.setValue(this.certification.certificationTitle);
        this.form.controls.providerId.setValue(this.certification.association.id);

        this.form.controls.certificationTitle.disable();
        this.form.controls.providerId.disable();

        const domains = this.certDomains as any[];
        if(domains) {
            const domain = domains.find(d => d.id == this.domainId);

            this.domains.at(0).get('domainName').setValue(domain.domainName);
            this.domains.at(0).get('id').setValue(domain.id);

            let i = 0;
            domain.objectives.forEach((r: { id:any; objectiveName: any; term: any; deletedAt: any }) => {
                this.addObjective(0);
                this.objectives(0).at(i).get('id').setValue(r.id);
                this.objectives(0).at(i).get('objectiveName').setValue(r.objectiveName);
                this.objectives(0).at(i).get('term').setValue(r.term);
                i++;
            });

        }

    }

    removeObjective(domainIndex: number, objectiveIndex: number) {
        const objectives = this.domains.at(domainIndex).get('objectives') as FormArray;
        if(objectives.length > 1) {
            objectives.removeAt(objectiveIndex);
        } else {
            this.showAlertDark('Warning', '', 'Cannot delete last remaining objective');
            return;
        }
    }

    saveDomain() {
        const validation = this.check(this.form);
        if (validation) {
            this.showAlertDark('Warning', '', 'Please complete all required fields.');
            return;
        }

        const screenWidth = window.innerWidth;
        const dialogWidth = screenWidth < 768 ? '90vw' : '800px';
        const confirm = this.dialog.open(ConfirmationDialog, {
            width: dialogWidth,
            maxWidth: '100%',
            data: {
                message: "Update Certification Domain",
                subMessage: "Are you sure want to update certification domain?",
                buttonOkLabel: 'Yes, Update',
                buttonCancelLabel: 'Cancel',
                theme: 'dark'
            }
        });
        confirm.componentInstance.result.subscribe(r => {
            if(r) {
                let objectives = this.domains.at(0).get('objectives') as FormArray;
                let remaining: any[] = [];
                for(let i = 0; i < objectives.length; i++) {
                    const objectiveFG = objectives.at(i) as FormGroup;
                    if(!objectiveFG.get('id').value) {
                        this.addNewObjective(0, objectiveFG.get('objectiveName').value, objectiveFG.get('term').value);
                    } else {
                        remaining.push(objectiveFG.value);
                    }
                }

                const remainingObjective = new Set(remaining.map(item => item.id));

                const removedObjectives = this.certification.domains[0].objectives.filter((item: { id: any; }) => !remainingObjective.has(item.id));

                for(let i = 0; i < removedObjectives.length; i++) {
                    this.addRemovedObjective(0, removedObjectives[i]);
                }

                let payload = this.form.value;
                payload['id'] = this.certification.id;
                payload['certificationTitle'] = this.certification.certificationTitle;
                payload['providerId'] = this.certification.association.id;

                this.certificationService.updateCertificationDomain(this.form.value, this.certification.id, this.domainId).then(_ => {
                    this.showSnackbarDark('Certification', 'Certification domain has been updated successfully', '');
                    this.router.navigate([`/office/new-certification/detail/${this.certification.id}`], {queryParams: {tab: 'domains'}}).then();
                })


            }
        })
    }

}
